/**
 * AI design assistant: turns styling requests into structured operations.
 *
 *   "Apply a cinematic medical theme"            → theme medical-cinema
 *   "Add smooth transitions to all slides"       → transition fade (all)
 *   "Make the background dark blue"              → background midnight-blue
 *   "Animate the labels one by one"              → click builds on labels
 *   "Create a smooth zoom between these two histology slides" → morph
 *
 * Parsing is local and deterministic, so it is instant, works offline and
 * can never change anything other than presentation styling.
 */

import { TRANSITIONS } from "./transitions";

const THEME_WORDS = [
  { id: "medical-cinema", re: /\b(medical\s+cinema|cinematic\s+medical|medical\s+cinematic|cinematic\s+theme|cinema)\b/ },
  { id: "apple-minimal", re: /\b(apple|minimal(ist)?|keynote|clean\s+white)\b/ },
  { id: "scientific-dark", re: /\b(scientific\s+dark|dark\s+(theme|mode|look|style)|high\s+contrast)\b/ },
  { id: "aurora-glass", re: /\b(aurora|glass(y)?)\b/ },
  { id: "academic-professional", re: /\b(academic|university|professional|formal|lecture\s+style)\b/ },
  { id: "nature-biology", re: /\b(nature|biology|organic|botanical)\b/ },
  { id: "premium-black", re: /\b(premium|luxury|elegant\s+black|black\s+theme)\b/ },
  { id: "modern-editorial", re: /\b(editorial|magazine)\b/ },
];

const TRANSITION_WORDS = [
  { id: "none", re: /\b(no|remove|disable|turn\s+off|without)\b[^.]*\btransitions?\b|\btransitions?\b[^.]*\b(off|none)\b/ },
  { id: "morph", re: /\bmorph(ing)?\b|\bzoom\s+between\b|\bsmooth\s+zoom\b/ },
  { id: "cube", re: /\bcube\b/ },
  { id: "parallax", re: /\bparallax\b/ },
  { id: "depth", re: /\bdepth\b/ },
  { id: "reveal", re: /\b(reveal|wipe)\b/ },
  { id: "blur", re: /\bblur\b/ },
  { id: "spotlight", re: /\bspotlight\b/ },
  { id: "pan", re: /\b(pan|camera\s+pan)\b/ },
  { id: "flip", re: /\bflip\b/ },
  { id: "film", re: /\b(film|dissolve)\b/ },
  { id: "zoom", re: /\b(zoom|cinematic)\b/ },
  { id: "fade", re: /\b(fade|smooth|subtle|simple|crossfade)\b/ },
];

const BACKGROUND_WORDS = [
  { presetId: "midnight-blue", re: /\b(dark\s+blue|navy|midnight)\b/ },
  { presetId: "deep-purple", re: /\b(purple|violet)\b/ },
  { presetId: "animated-aurora", re: /\banimated\s+aurora\b/ },
  { presetId: "aurora-gradient", re: /\baurora\b/ },
  { presetId: "ocean-blue", re: /\b(ocean|blue)\b/ },
  { presetId: "soft-pastel", re: /\bpastel\b/ },
  { presetId: "sunset-glow", re: /\b(sunset|orange|warm)\b/ },
  { presetId: "dark-emerald", re: /\b(green|emerald)\b/ },
  { presetId: "minimal-white", re: /\b(white|light)\b/ },
  { presetId: "graphite-black", re: /\b(black|graphite|charcoal|dark)\b/ },
  { presetId: "moving-particles", re: /\bparticles?\b/ },
  { presetId: "dna-helix", re: /\bdna\b/ },
  { presetId: "neuron-network", re: /\b(neuron|neural|nerve)\b/ },
  { presetId: "cells", re: /\b(cell|cells|cellular)\b/ },
  { presetId: "tissue", re: /\btissue\b/ },
  { presetId: "histology-texture", re: /\bhistology\b/ },
  { presetId: "medical-grid", re: /\b(grid|medical)\b/ },
  { presetId: "anatomy-silhouette", re: /\banatomy\b/ },
];

const STRONG_DESIGN_SIGNAL =
  /\b(theme|transitions?|backgrounds?|animat(e|ion|ions)|morph|typography|fonts?|one\s+by\s+one|zoom\s+between|smooth\s+zoom|parallax)\b/;
const SOFT_DESIGN_SIGNAL =
  /\b(look|looks|style|styling|design)\b.*\b(professional|cinematic|modern|polished|better|nicer|beautiful|elegant|premium)\b|\b(professional|cinematic|modern|polished|premium)\b.*\b(look|style|styling|design)\b/;
// Content edits ("rewrite slide 2 in a professional tone") stay with the deck assistant.
const CONTENT_EDIT = /\b(rewrite|simplify|shorten|expand|rename|retitle|translate|summari[sz]e|tone|wording)\b/;
const DESIGN_SIGNAL = {
  test: (t) => STRONG_DESIGN_SIGNAL.test(t) || (SOFT_DESIGN_SIGNAL.test(t) && !CONTENT_EDIT.test(t)),
};

/** Quick check used by the chat to decide whether to route here. */
export const looksLikeDesignRequest = (text) => {
  const t = String(text || "").toLowerCase().trim();
  if (!t) return false;
  if (/^__design__:/.test(t)) return true;
  if (/^(undo|redo)\b.*\b(design|theme|style|transition|background|animation|change)s?\b/.test(t)) return true;
  if (/^preview\s+(the\s+)?(presentation|slides?|design|deck)\b/.test(t)) return true;
  // New content belongs to the regular presentation assistant.
  if (/\b(make|create|generate|build)\s+(a\s+|an\s+|new\s+)?(presentation|deck|quiz)\b/.test(t)) return false;
  if (/\b(add|insert|remove|delete)\s+(a\s+|the\s+)?slide\b/.test(t)) return false;
  return DESIGN_SIGNAL.test(t);
};

const parseScope = (t) => {
  if (/\b(this|current)\s+slide\b|\bon\s+slide\b(?!s)/.test(t)) return "current";
  const m = t.match(/\bslides?\s+((?:\d+\s*(?:,|and|-|to)?\s*)+)/);
  if (m) {
    const nums = [];
    m[1].replace(/(\d+)\s*(?:-|to)\s*(\d+)|(\d+)/g, (_, a, b, c) => {
      if (a && b) for (let i = Number(a); i <= Number(b); i += 1) nums.push(i);
      else if (c) nums.push(Number(c));
      return _;
    });
    if (nums.length) return { slides: nums };
  }
  return "all";
};

const parseDuration = (t) => {
  const ms = t.match(/(\d{3,4})\s*ms\b/);
  if (ms) return Number(ms[1]);
  const sec = t.match(/(\d+(?:\.\d+)?)\s*(s|sec|secs|seconds?)\b/);
  if (sec) return Math.round(Number(sec[1]) * 1000);
  if (/\b(slower|slow)\b/.test(t)) return 1100;
  if (/\b(faster|quick|quicker|snappy)\b/.test(t)) return 400;
  return null;
};

/**
 * Parse a request into operations. Returns { ops: [...], unknown: boolean }.
 * Operation shapes:
 *   { kind: "theme", themeId, scope }
 *   { kind: "transition", type, duration?, easing?, scope }
 *   { kind: "background", background, scope }
 *   { kind: "animate", target: "labels"|"text"|"images"|"all", trigger, effect }
 *   { kind: "clearAnimations" }
 *   { kind: "morph" }
 *   { kind: "undo" } | { kind: "redo" } | { kind: "preview" }
 */
export const parseDesignRequest = (text) => {
  const raw = String(text || "").trim();
  const t = raw.toLowerCase();
  const ops = [];

  const command = t.match(/^__design__:(\w+)/);
  if (command) return { ops: [{ kind: command[1] }], unknown: false };
  if (/^undo\b/.test(t)) return { ops: [{ kind: "undo" }], unknown: false };
  if (/^redo\b/.test(t)) return { ops: [{ kind: "redo" }], unknown: false };
  if (/^(preview|play|present)\b/.test(t)) return { ops: [{ kind: "preview" }], unknown: false };

  const scope = parseScope(t);
  const mentionsTransition = /\btransitions?\b/.test(t);
  const mentionsBackground = /\bbackgrounds?\b/.test(t);
  const mentionsAnimation = /\b(animat(e|ion|ions)|one\s+by\s+one|build)\b/.test(t);

  // Morph / smooth zoom between slides.
  if (/\bmorph|zoom\s+between|smooth\s+zoom\b/.test(t)) {
    ops.push({ kind: "morph" });
  }

  // Theme.
  const wantsTheme = /\btheme|look|style|design|professional|cinematic\b/.test(t) && !mentionsBackground;
  if (wantsTheme && !ops.some((o) => o.kind === "morph")) {
    const match = THEME_WORDS.find((w) => w.re.test(t));
    if (match && !(mentionsTransition && !/\btheme|look|style|design|professional\b/.test(t))) {
      ops.push({ kind: "theme", themeId: match.id, scope });
    }
  }

  // Transitions.
  if (mentionsTransition) {
    const match = TRANSITION_WORDS.find((w) => w.re.test(t));
    const type = match ? match.id : "fade";
    const easing = /\bspring|bouncy\b/.test(t)
      ? "spring"
      : /\bcinematic\b/.test(t)
        ? "cinematic"
        : null;
    ops.push({
      kind: "transition",
      type: TRANSITIONS.some((x) => x.id === type) ? type : "fade",
      duration: parseDuration(t),
      easing,
      scope,
    });
  }

  // Background.
  if (mentionsBackground) {
    const hex = raw.match(/#([0-9a-f]{6}|[0-9a-f]{3})\b/i);
    if (hex) {
      ops.push({ kind: "background", background: { kind: "solid", color: `#${hex[1]}` }, scope });
    } else {
      const match = BACKGROUND_WORDS.find((w) => w.re.test(t));
      if (match) {
        ops.push({ kind: "background", background: { kind: "preset", presetId: match.presetId }, scope });
      }
    }
  }

  // Element animations.
  if (mentionsAnimation && !ops.some((o) => o.kind === "morph")) {
    if (/\b(remove|clear|delete|no|stop)\b/.test(t)) {
      ops.push({ kind: "clearAnimations" });
    } else {
      const target = /\blabels?|annotations?|pins?\b/.test(t)
        ? "labels"
        : /\b(images?|pictures?|photos?|figures?)\b/.test(t)
          ? "images"
          : /\b(text|bullets?|points?|paragraphs?|titles?)\b/.test(t)
            ? "text"
            : "all";
      const oneByOne = /\bone\s+(by|after)\s+(one|another)|sequential(ly)?|step\s+by\s+step|on\s+click\b/.test(t);
      const auto = /\bautomatic(ally)?|by\s+itself|without\s+click/.test(t);
      const effect = /\bfade\b/.test(t)
        ? "fadeIn"
        : /\bzoom\b/.test(t)
          ? "zoomIn"
          : /\bblur\b/.test(t)
            ? "blurIn"
            : /\bslide\s+in\b/.test(t)
              ? "slideIn"
              : /\bspotlight\b/.test(t)
                ? "spotlight"
                : /\bfloat/.test(t)
                  ? "float"
                  : target === "labels"
                    ? "fadeIn"
                    : "riseUp";
      ops.push({
        kind: "animate",
        target,
        effect,
        trigger: auto ? "after" : oneByOne || target === "labels" ? "click" : "after",
      });
    }
  }

  // "Make it look more professional / cinematic" with no other signal.
  if (!ops.length && /\b(professional|polished|better|nicer|modern|beautiful)\b/.test(t)) {
    ops.push({ kind: "theme", themeId: /\bcinematic|medical\b/.test(t) ? "medical-cinema" : "academic-professional", scope: "all" });
    ops.push({ kind: "transition", type: "fade", duration: 650, easing: "smooth", scope: "all" });
  }
  if (!ops.length && /\bcinematic\b/.test(t)) {
    ops.push({ kind: "theme", themeId: "medical-cinema", scope: "all" });
  }

  return { ops, unknown: ops.length === 0 };
};

export const DESIGN_SUGGESTIONS = [
  "Make my presentation look more professional",
  "Apply a cinematic medical theme",
  "Add smooth transitions to all slides",
  "Make the background dark blue",
  "Animate the labels one by one",
  "Create a smooth zoom between these two histology slides",
];
